import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Plus, Search, Filter, Sparkles, FileText, MessageSquare, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { AIButton, AIBadge, AIPanel, AIExplanationBox } from '../components/AIComponents';
import { usePagination } from '../hooks/usePagination';

export function Invoices() {
  const { 
    data: invoices, 
    loading, 
    loadingMore,
    error, 
    hasMore, 
    loadMore, 
    fetchData,
    pagination
  } = usePagination('invoices', { initialLimit: 25, orgAsQueryParam: true });

  useEffect(() => {
    fetchData(1, false);
  }, [fetchData]);

  // Transform invoices for display
  const transformedInvoices = invoices.map(inv => ({
    id: inv.id,
    invoiceNumber: inv.invoice_number || `INV-${inv.id?.slice(-4) || '0000'}`,
    client: inv.client_name || 'Client',
    title: inv.description || inv.notes || 'Invoice',
    amount: inv.amount_gross || inv.amount_net || inv.amount || 0,
    status: inv.status?.toLowerCase() || 'draft',
    date: inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB') : 'N/A',
    dueDate: inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB') : null
  }));

  // Calculate totals
  const totalPending = transformedInvoices
    .filter(i => i.status === 'pending' || i.status === 'sent')
    .reduce((sum, i) => sum + i.amount, 0);
  const totalOverdue = transformedInvoices
    .filter(i => i.status === 'overdue')
    .reduce((sum, i) => sum + i.amount, 0);
  const totalPaid = transformedInvoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Invoice Life</h1>
          <p>Track payments and manage invoices</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" disabled><Plus size={16} />New Invoice</button>
        </div>
      </div>

      {/* AI Invoice Assistant */}
      <AIPanel 
        title="AI Invoice Assistant" 
        description="AI helps create and manage invoices with smart suggestions."
        className="mb-xl"
      >
        <div className="ai-actions-grid">
          <AIButton icon={FileText} tooltip="AI generation will be enabled soon">
            Generate invoice from quote
          </AIButton>
          <AIButton icon={MessageSquare} tooltip="AI generation will be enabled soon">
            Rewrite invoice wording
          </AIButton>
          <AIButton icon={Sparkles} tooltip="AI generation will be enabled soon">
            Explain invoice to client
          </AIButton>
        </div>
      </AIPanel>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {!loading && !error && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-warning)' }}>£{totalPending.toLocaleString()}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Pending</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-danger)' }}>£{totalOverdue.toLocaleString()}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Overdue</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-success)' }}>£{totalPaid.toLocaleString()}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Paid (This Month)</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>{pagination.total}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Total Invoices</div>
            </GlassPanel>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) var(--space-md)', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)' }}>
              <Search size={16} style={{ color: 'var(--text-dimmed)' }} />
              <input type="text" placeholder="Search invoices..." style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none' }} />
            </div>
            <button className="btn btn-secondary"><Filter size={16} />Filter</button>
          </div>

          <GlassPanel title="All Invoices" titleIcon={Receipt}>
            {transformedInvoices.length > 0 ? (
              <>
                <div className="list-container" data-testid="invoices-list">
                  {transformedInvoices.map((invoice) => (
                    <Link key={invoice.id} to={`/invoices/${invoice.id}`} className="list-item" data-testid={`invoice-item-${invoice.id}`}>
                      <div className="list-item-content">
                        <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                          {invoice.invoiceNumber}
                          <AIBadge text="AI" />
                        </div>
                        <div className="list-item-subtitle">{invoice.client} • {invoice.title}</div>
                      </div>
                      <div className="list-item-meta">
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{invoice.date}</span>
                        <span className={`status-badge ${invoice.status === 'paid' ? 'paid' : invoice.status === 'overdue' ? 'overdue' : 'pending'}`}>{invoice.status}</span>
                        <span className="list-item-amount">£{invoice.amount.toLocaleString()}</span>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-lg)',
                    borderTop: '1px solid var(--glass-border)'
                  }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn btn-secondary"
                      data-testid="load-more-invoices"
                      style={{ minWidth: '200px' }}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Load More ({pagination.total - transformedInvoices.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Pagination Info */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: 'var(--space-md)',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)'
                }}>
                  Showing {transformedInvoices.length} of {pagination.total} invoices
                </div>
              </>
            ) : (
              <EmptyState icon={Receipt} title="No invoices yet" description="Create your first invoice to get started" />
            )}
          </GlassPanel>

          <AIExplanationBox>
            AI can automatically generate invoices from accepted quotes and help explain complex pricing to clients.
          </AIExplanationBox>
        </>
      )}
    </div>
  );
}

export default Invoices;
